import type { Kks } from "@shared/types/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { getPid, updateKks } from "../api";
import { ensureKksDefaults, sortKks } from "../utils/kks";

const AUTOSAVE_DEBOUNCE_MS = 3_000;
const AUTOSAVE_BADGE_DURATION_MS = 2_000;

export type AutosaveStatus = "idle" | "saved" | "error";

export function usePidData(
  powerPlantId: string,
  systemId: string,
  pidId: string,
) {
  const queryClient = useQueryClient();
  const [kks, setKks] = useState<Kks[]>([]);
  const [dirty, setDirty] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>("idle");

  const { data: pid, isLoading } = useQuery({
    queryKey: ["pid", pidId],
    queryFn: () => getPid(powerPlantId, systemId, pidId),
  });

  // Initialize kks from server data
  useEffect(() => {
    if (pid?.kks) {
      setKks(sortKks(pid.kks.map(ensureKksDefaults)));
      setDirty(false);
    }
  }, [pid]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["pid", pidId] });
    queryClient.invalidateQueries({
      queryKey: ["pids", powerPlantId, systemId],
    });
    queryClient.invalidateQueries({ queryKey: ["systems", powerPlantId] });
    queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
  }, [queryClient, pidId, powerPlantId, systemId]);

  const saveMut = useMutation({
    mutationFn: () => updateKks(powerPlantId, systemId, pidId, kks),
    onSuccess: () => {
      invalidateAll();
      setDirty(false);
    },
    onError: () => {
      setAutosaveStatus("error");
    },
  });

  // Autosave 3 s after the last change when dirty (skip if PID is locked)
  const saveMutRef = useRef(saveMut);
  saveMutRef.current = saveMut;
  useEffect(() => {
    if (!dirty) return;
    if (pid?.locked) return;
    const id = setTimeout(() => {
      if (!saveMutRef.current.isPending) {
        saveMutRef.current.mutate();
        setAutosaveStatus("saved");
        setTimeout(() => setAutosaveStatus("idle"), AUTOSAVE_BADGE_DURATION_MS);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [dirty, kks, pid?.locked]);

  return {
    pid,
    isLoading,
    kks,
    setKks,
    dirty,
    setDirty,
    autosaveStatus,
    saveMut,
  };
}
